<script setup lang="ts">
import { watchDebounced } from "@vueuse/core";
import type { Blog, BlogsApiResponse } from "~/types";

definePageMeta({
  layout: "empty",
  auth: false,
});

const route = useRoute();
const router = useRouter();

const showSearch = ref(false);
const currentPage = ref(1);
const pageSize = ref(6);
const search = ref<string>((route.query.search as string) || "");
const debounceSearch = ref(search.value);
const order = ref<string>((route.query.order as string) || "newest");
const tabs = ref<string[]>(
  ((route.query.tabs as string) || "").split(",").filter(Boolean),
);

const { data: blogOrder } =
  await useFetch<{ label: string; value: string }[]>("/api/blog-order");
const { data: blogTags } =
  await useFetch<{ label: string; value: string }[]>("/api/blog-tag");

const selectedOrderLabel = computed(() => {
  return (
    blogOrder.value?.find((item) => item.value === order.value)?.label || ""
  );
});

const { data, status, refresh } = await usePublicFetch<BlogsApiResponse>(
  () =>
    `blogs?page=${currentPage.value}&pageSize=${pageSize.value}` +
    (debounceSearch.value ? `&search=${debounceSearch.value}` : "") +
    (order.value ? `&order=${order.value}` : "") +
    (tabs.value.length ? `&tabs=${tabs.value.join(",")}` : ""),
  {
    key:
      `blogs-page-${currentPage.value}` +
      `-search-${debounceSearch.value}` +
      `-order-${order.value}` +
      `-tabs-${tabs.value.join(",")}`,
  },
);

const blogs = computed<Blog[]>(() => data.value?.data || []);
const pagination = computed(() => ({
  page: data.value?.page || 1,
  pageSize: data.value?.pageSize || 6,
  total: data.value?.total || 0,
}));

const loadData = (event: { page: number }) => {
  currentPage.value = event.page + 1;
  refresh();
};

watchDebounced(
  search,
  (newValue) => {
    currentPage.value = 1;
    debounceSearch.value = newValue;

    router.replace({
      query: {
        ...route.query,
        search: newValue || undefined,
      },
    });
  },
  { debounce: 500, maxWait: 6000 },
);

watch(tabs, (newValue) => {
  currentPage.value = 1;

  router.replace({
    query: {
      ...route.query,
      tabs: newValue.length ? newValue.join(",") : undefined,
    },
  });
});

watch(selectedOrderLabel, (newValue) => {
  currentPage.value = 1;

  router.replace({
    query: {
      ...route.query,
      order: order.value,
    },
  });
});

// --- SEO ---
useHead({
  link: [{ rel: "canonical", href: "https://unilinks.co/blogs" }],
});

useSeoMeta({
  title: "Study Abroad Tips, University Rankings & Visa Guides | UniLinks Blog",
  ogTitle:
    "Study Abroad Tips, University Rankings & Visa Guides | UniLinks Blog",
  description:
    "Read about Thai higher education, top 10 universities, EduRank Rankings, student life, and practical visa tips to help you study in Thailand smoothly.",
  ogDescription:
    "Read about Thai higher education, top 10 universities, EduRank Rankings, student life, and practical visa tips to help you study in Thailand smoothly.",
  ogImage: META_IMAGE_URL,
  ogUrl: "https://unilinks.co/blogs",
  twitterCard: "summary_large_image",
  twitterTitle:
    "Study Abroad Tips, University Rankings & Visa Guides | UniLinks Blog",
  twitterDescription:
    "Read about Thai higher education, top 10 universities, EduRank Rankings, student life, and practical visa tips to help you study in Thailand smoothly.",
  twitterImage: META_IMAGE_URL,
});
</script>
<style></style>
<template>
  <NuxtLayout name="primary">
    <div class="bg-gray-50 mx-4">
      <div class="mx-auto lg:w-full max-w-7xl lg:min-h-[760px] w-full lg:px-4">
        <div class="mb-4">
          <!-- Mobile Filters Accordion -->
          <div class="block lg:hidden">
            <div class="mt-5 flex items-center justify-between">
              <h1 class="text-xl font-bold text-primary-500">
                Explore Our Insights
              </h1>
              <Button
                v-if="!showSearch"
                icon="pi pi-search"
                text
                rounded
                aria-label="Filter"
                @click="showSearch = true"
              />
              <Button
                v-else
                icon="pi pi-times"
                text
                rounded
                aria-label="Filter"
                @click="showSearch = false"
              />
            </div>

            <Accordion :activeIndex="0" v-if="showSearch">
              <AccordionTab
                :pt="{
                  content: '!bg-gray-50 !px-0',
                }"
              >
                <div class="space-y-3">
                  <IconField class="w-full !text-sm my-4">
                    <InputIcon class="pi pi-search" />
                    <InputText
                      v-model="search"
                      placeholder="Search blogs"
                      class="w-full !text-sm"
                    />
                  </IconField>

                  <div class="grid grid-cols-2 gap-2">
                    <MultiSelect
                      :options="blogTags || []"
                      v-model="tabs"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select Categories"
                      :maxSelectedLabels="3"
                      class="w-full !text-sm"
                      :pt="{
                        header: 'hidden',
                        label: 'text-sm py-2 px-4 truncate',
                      }"
                    />

                    <Select
                      v-model="order"
                      :options="blogOrder || []"
                      panelClass="!text-sm"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Newest first"
                      appendTo="body"
                      class="w-full !text-sm"
                    >
                      <template #value>
                        <i
                          class="pi pi-arrow-down mr-2 text-gray-400 text-sm"
                        ></i>
                        <span class="text-card-title">{{
                          selectedOrderLabel
                        }}</span>
                      </template>
                      <template #dropdownicon>
                        <i class="pi pi-chevron-down text-gray-400"></i>
                      </template>
                    </Select>
                  </div>
                </div>
              </AccordionTab>
            </Accordion>
          </div>

          <!-- Desktop Filters -->
          <div
            class="hidden lg:flex flex-col lg:flex-row justify-between items-center gap-4 py-5 pt-12"
          >
            <!-- Title -->
            <h1 class="text-3xl font-bold text-primary-500">
              Explore Our Insights
            </h1>

            <!-- Search & Filters -->
            <div class="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <IconField class="w-full sm:w-60 !text-sm">
                <InputIcon class="pi pi-search" />
                <InputText
                  v-model="search"
                  placeholder="Search blogs"
                  class="w-full !text-sm"
                />
              </IconField>

              <MultiSelect
                :options="blogTags || []"
                v-model="tabs"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Categories"
                :maxSelectedLabels="3"
                class="w-full sm:w-40 !text-sm"
                :pt="{
                  header: 'hidden',
                  label: 'text-sm py-2 px-4 truncate',
                }"
              />

              <Select
                v-model="order"
                :options="blogOrder || []"
                panelClass="!text-sm"
                optionLabel="label"
                optionValue="value"
                placeholder="Newest first"
                appendTo="body"
                class="w-full sm:w-40 !text-sm"
              >
                <template #value>
                  <i class="pi pi-arrow-down mr-2 text-gray-400 text-sm"></i>
                  <span class="text-card-title">{{ selectedOrderLabel }}</span>
                </template>
                <template #dropdownicon>
                  <i class="pi pi-chevron-down text-gray-400"></i>
                </template>
              </Select>
            </div>
          </div>
        </div>

        <div>
          <div v-if="blogs.length">
            <div
              class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8"
            >
              <BlogCard v-for="blog in blogs" :key="blog.id" :data="blog" />
            </div>
            <Paginator
              :rows="pageSize"
              :totalRecords="pagination.total"
              :first="(currentPage - 1) * pageSize"
              @page="loadData"
              class="hidden lg:block mt-10"
            />
            <Paginator
              class="w-full block lg:hidden mt-5"
              template="PrevPageLink CurrentPageReport NextPageLink"
              :rows="pageSize"
              :totalRecords="pagination?.total"
              @page="loadData"
              currentPageReportTemplate="Page {currentPage} of {totalPages}"
              :pt="{
                root: 'px-4 w-full pb-4',
                content: 'flex justify-between items-center',
                prev: 'px-2.5 py-1.5 rounded-md border border-gray-300',
                next: 'px-2.5 py-1.5 rounded-md border border-gray-300',
              }"
            >
              <template #nexticon>
                <i class="pi pi-arrow-right" />
              </template>
              <template #previcon>
                <i class="pi pi-arrow-left" />
              </template>
            </Paginator>
          </div>
          <div v-else>
            <p class="text-gray-500 h-80 flex items-center justify-center">
              No blogs created yet
            </p>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </NuxtLayout>
</template>
